import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { Button } from '~/components';
import { formatCalendarDate, formatDollars } from '~/features/employer/billing/format';
import type { ListingActivationPreview } from '~/features/employer/billing/types';
import { useManagePaymentMethod } from '~/features/employer/billing/useManagePaymentMethod';

const TAGS = CT.marketResponses;

const RENEWAL_COPY =
  "It renews every 30 days while active. Paused or closed listings aren't renewed, and listing fees aren't refundable.";

/** [MCV-1933] The sentence an employer confirms before a charged listing goes live. */
export const listingFeeMessage = (preview: ListingActivationPreview) => {
  const amount = formatDollars(preview.amountCents);
  if (preview.reason === 'ENTERPRISE_OVERAGE' && preview.plan) {
    return `You have ${preview.plan.includedActiveListings} active listings, the most your enterprise plan covers. This one costs ${amount} for 30 days. ${RENEWAL_COPY}`;
  }
  const through = preview.windowEnd ? ` (through ${formatCalendarDate(preview.windowEnd)})` : '';
  return `Publishing this listing costs ${amount} for 30 days${through}. ${RENEWAL_COPY}`;
};

type ListingFeeDialogProps = {
  /** The preview that said a charge is due; null closes the dialog. */
  preview: ListingActivationPreview | null;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * [MCV-1933] Confirms the listing fee before an employer listing is published. When no payment
 * method is on file it offers the same add-a-card flow as the commitment dialog instead of Publish.
 */
export const ListingFeeDialog = ({ preview, onConfirm, onCancel }: ListingFeeDialogProps) => {
  const managePaymentMethod = useManagePaymentMethod();
  const missingPaymentMethod = preview != null && !preview.paymentMethodOnFile;

  return (
    <Dialog open={preview != null} onClose={onCancel} className="relative z-[60]">
      <DialogBackdrop className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            data-cy={TAGS.listingFeeDialog}
            className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
          >
            {preview && (
              <>
                <div className="flex items-start gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-t-900">
                    <CreditCardIcon className="size-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <DialogTitle className="text-lg font-semibold text-grey-100">
                      Publish listing
                    </DialogTitle>
                    <p className="mt-2 body-text text-grey-400 break-words">
                      {listingFeeMessage(preview)}
                    </p>
                    {missingPaymentMethod && (
                      <p role="alert" className="mt-3 body-text text-error">
                        Add a payment method before publishing this listing.
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-6 flex gap-3 justify-end">
                  <Button
                    type="button"
                    colorVariant="secondary"
                    cyTag={TAGS.listingFeeCancelBtn}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  {missingPaymentMethod ? (
                    <Button type="button" onClick={managePaymentMethod}>
                      <span className="inline-flex items-center gap-2">
                        <CreditCardIcon className="h-5 w-5" />
                        Add payment method
                      </span>
                    </Button>
                  ) : (
                    <Button type="button" cyTag={TAGS.listingFeeConfirmBtn} onClick={onConfirm}>
                      Publish
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
